/**
 * @file PostFxService.ts
 * @role Owns every `pixi-filters` post-processing effect used by win presentation: symbol
 *       glow, a scoped blur for a layer, a bloom "punch" timeline, a board screen-shake, and
 *       a full-bleed flash. Every apply-style method has a matching clear/cleanup path so
 *       filters and GSAP tweens never outlive the effect they belong to (Invariant 12/18).
 *       Presets/tunables live in fx.config.json (Zod-validated, mirrors ParticleService).
 * @owns Live filter instances (tracked per-container) and their GSAP tweens/timelines.
 * @reads fx.config.json (or injected raw config).
 * @neverDoes Decide which symbols/tiers get which effect. Touch HUD.
 *            Read raw rAF/Date for motion (GSAP drives polish-only tweens, ADR 0003).
 * @invariants 8 (render-only effect), 10 (config validated), 12/18 (full cleanup).
 * @see src/game/reels/SymbolEffectController.ts, docs/PLAN.md (Win Celebration FX)
 */
import { BlurFilter, Container, Graphics, Rectangle, type Filter } from 'pixi.js';
import { GlowFilter, AdvancedBloomFilter } from 'pixi-filters';
import { gsap } from 'gsap';
import { z } from 'zod';
import fxConfigRaw from '@config/fx.config.json';
import { GameError } from '@core/errors/GameError';

export interface GlowPresetRef {
  readonly id: string;
}

const GlowPresetSchema = z.object({
  color: z.number().int().min(0).max(0xffffff),
  distance: z.number().positive(),
  outerStrength: z.number().nonnegative(),
  innerStrength: z.number().nonnegative(),
  alpha: z.number().min(0).max(1),
});

const ScopedBlurConfigSchema = z.object({
  strength: z.number().positive(),
  quality: z.number().int().positive(),
});

const BloomPulseConfigSchema = z.object({
  threshold: z.number().min(0).max(1),
  bloomScale: z.number().positive(),
  brightness: z.number().positive(),
  blur: z.number().positive(),
  strengthMultiplier: z.number().positive(),
  punchMs: z.number().positive(),
});

const ScreenShakeConfigSchema = z.object({
  amplitude: z.number().nonnegative(),
  durationMs: z.number().positive(),
  cycles: z.number().int().positive(),
});

const FrameHaloConfigSchema = z.object({
  color: z.number().int().min(0).max(0xffffff),
  alpha: z.number().min(0).max(1),
  ringWidthRatio: z.number().positive().max(0.1),
  paddingRatio: z.number().min(0).max(0.2),
  expansionRatio: z.number().positive().max(0.5),
  durationMs: z.number().positive(),
  pulseCount: z.number().int().min(1).max(10),
  staggerMs: z.number().nonnegative(),
  ease: z.enum(['sine.out', 'power2.out', 'power3.out']),
});

const FlashConfigSchema = z.object({
  color: z.number().int().min(0).max(0xffffff),
  peakAlpha: z.number().min(0).max(1),
  inMs: z.number().positive(),
  outMs: z.number().positive(),
});

export const FxConfigSchema = z.object({
  quality: z.enum(['high', 'medium', 'low']),
  defaultGlowPreset: z.string(),
  glowPresets: z.record(z.string(), GlowPresetSchema),
  scopedBlur: ScopedBlurConfigSchema,
  bloomPulse: BloomPulseConfigSchema,
  screenShake: ScreenShakeConfigSchema,
  frameHalo: FrameHaloConfigSchema,
  flash: FlashConfigSchema,
});

export type GlowPresetConfig = z.infer<typeof GlowPresetSchema>;
export type FxConfig = z.infer<typeof FxConfigSchema>;
export type FxQuality = FxConfig['quality'];

export function validateFxConfig(raw: unknown): FxConfig {
  const parsed = FxConfigSchema.safeParse(raw);
  if (!parsed.success) {
    throw new GameError('CONFIG_LOAD_FAILED', `Invalid fx config: ${parsed.error.message}`, {
      cause: parsed.error,
    });
  }
  return parsed.data;
}

export class PostFxService {
  private readonly config: FxConfig;
  private qualityOverride: FxQuality | undefined;

  private readonly glowFilters = new WeakMap<Container, GlowFilter>();
  private readonly glowTweens = new WeakMap<Container, gsap.core.Tween>();
  private readonly glowFilterAreas = new WeakMap<Container, Rectangle | null>();
  private readonly blurFilters = new WeakMap<Container, BlurFilter>();
  private readonly blurFilterAreas = new WeakMap<Container, Rectangle | null>();
  private readonly bloomFilters = new WeakMap<Container, AdvancedBloomFilter>();
  private readonly bloomTimelines = new WeakMap<Container, gsap.core.Timeline>();
  private readonly bloomFilterAreas = new WeakMap<Container, Rectangle | null>();
  private readonly shakeTweens = new WeakMap<Container, gsap.core.Timeline>();
  private readonly shakeOrigins = new WeakMap<Container, { x: number; y: number }>();
  private readonly frameHalos = new WeakMap<
    Container,
    { root: Container; timeline: gsap.core.Timeline }
  >();
  private readonly flashTweens = new WeakMap<Container, gsap.core.Timeline>();

  constructor(raw: unknown = fxConfigRaw) {
    this.config = validateFxConfig(raw);
  }

  get quality(): FxQuality {
    return this.qualityOverride ?? this.config.quality;
  }

  setQuality(quality: FxQuality): void {
    this.qualityOverride = quality;
  }

  get flashColor(): number {
    return this.config.flash.color;
  }

  /** Apply a preset-driven glow to `container`. Replaces any glow already applied by this service. */
  glow(container: Container, preset: GlowPresetRef): void {
    this.clearGlow(container);
    const presetConfig =
      this.config.glowPresets[preset.id] ?? this.config.glowPresets[this.config.defaultGlowPreset];
    if (!presetConfig) return;
    const filter = new GlowFilter({
      color: presetConfig.color,
      distance: presetConfig.distance,
      outerStrength: presetConfig.outerStrength,
      innerStrength: presetConfig.innerStrength,
      alpha: presetConfig.alpha,
    });
    this.glowFilters.set(container, filter);
    setBoundedFilterArea(this.glowFilterAreas, container, presetConfig.distance);
    container.filters = [...toFilterArray(container.filters), filter];
  }

  /** Remove only the glow filter this service applied; leaves any other filter untouched. */
  clearGlow(container: Container): void {
    this.glowTweens.get(container)?.kill();
    this.glowTweens.delete(container);
    const filter = this.glowFilters.get(container);
    if (!filter) return;
    this.glowFilters.delete(container);
    removeFilter(container, filter);
    restoreFilterArea(this.glowFilterAreas, container);
    filter.destroy();
  }

  /** Fade this service's glow strength before removing the filter at the reset boundary. */
  fadeGlow(container: Container, durationMs: number): gsap.core.Tween | undefined {
    const filter = this.glowFilters.get(container);
    if (!filter) return undefined;
    this.glowTweens.get(container)?.kill();
    const tween = gsap.to(filter, {
      outerStrength: 0,
      innerStrength: 0,
      duration: Math.max(1, durationMs) / 1000,
      ease: 'power2.inOut',
      onComplete: () => {
        if (this.glowFilters.get(container) !== filter) return;
        this.glowTweens.delete(container);
        this.glowFilters.delete(container);
        removeFilter(container, filter);
        restoreFilterArea(this.glowFilterAreas, container);
        filter.destroy();
      },
    });
    this.glowTweens.set(container, tween);
    return tween;
  }

  /** Blur a layer, scoped to `filterArea` so the cost stays bounded to the affected region. */
  scopedBlur(layer: Container, filterArea: Rectangle): void {
    this.clearBlur(layer);
    const filter = new BlurFilter({
      strength: this.config.scopedBlur.strength,
      quality: this.config.scopedBlur.quality,
    });
    this.blurFilters.set(layer, filter);
    this.blurFilterAreas.set(layer, layer.filterArea ?? null);
    layer.filterArea = filterArea;
    layer.filters = [...toFilterArray(layer.filters), filter];
  }

  /** Remove the scoped blur this service applied and clear the filter area it set. */
  clearBlur(layer: Container): void {
    const filter = this.blurFilters.get(layer);
    if (!filter) return;
    this.blurFilters.delete(layer);
    removeFilter(layer, filter);
    filter.destroy();
    restoreFilterArea(this.blurFilterAreas, layer);
  }

  /**
   * Bloom "punch": apply an AdvancedBloomFilter and pulse its strength without moving or
   * scaling the target. The target must contain visible renderables. Any prior pulse on this
   * layer is killed first so pulses never stack.
   */
  bloomPulse(layer: Container): gsap.core.Timeline {
    // kill() does not fire onComplete, so a pulse retriggered mid-flight must have its own
    // filter/scale cleaned up here first — otherwise filters stack and scale drifts.
    this.clearBloom(layer);

    const filter = new AdvancedBloomFilter({
      threshold: this.config.bloomPulse.threshold,
      bloomScale: 0,
      brightness: 1,
      blur: this.config.bloomPulse.blur,
    });
    this.bloomFilters.set(layer, filter);
    setBoundedFilterArea(this.bloomFilterAreas, layer, this.config.bloomPulse.blur * 2);
    layer.filters = [...toFilterArray(layer.filters), filter];
    const punchMs = this.config.bloomPulse.punchMs / 1000;
    const timeline = gsap.timeline({
      onComplete: () => {
        removeFilter(layer, filter);
        restoreFilterArea(this.bloomFilterAreas, layer);
        filter.destroy();
        this.bloomFilters.delete(layer);
        this.bloomTimelines.delete(layer);
      },
    });
    timeline
      .to(filter, {
        bloomScale: this.config.bloomPulse.bloomScale * this.config.bloomPulse.strengthMultiplier,
        brightness: this.config.bloomPulse.brightness,
        duration: punchMs / 2,
        ease: 'sine.out',
      })
      .to(filter, {
        bloomScale: 0,
        brightness: 1,
        duration: punchMs / 2,
        ease: 'sine.in',
      });
    this.bloomTimelines.set(layer, timeline);
    return timeline;
  }

  /** Cancel a bloom pulse immediately and remove its filter. */
  clearBloom(layer: Container): void {
    this.bloomTimelines.get(layer)?.kill();
    this.bloomTimelines.delete(layer);
    const filter = this.bloomFilters.get(layer);
    if (filter) {
      removeFilter(layer, filter);
      restoreFilterArea(this.bloomFilterAreas, layer);
      filter.destroy();
      this.bloomFilters.delete(layer);
    }
  }

  /**
   * Shake `boardContainer` around its current position. Only ever tweens the position of the
   * exact container passed in — callers must pass the ReelBoardView root, never the HUD.
   * `overrides` lets a caller (e.g. SymbolEffectController, driven by per-tier config) supply
   * its own amplitude/durationMs/cycles for this call; any field left unset falls back to
   * this service's own `fx.config.json` default.
   */
  screenShake(
    boardContainer: Container,
    overrides?: { amplitude?: number; durationMs?: number; cycles?: number },
  ): gsap.core.Timeline {
    // kill() does not fire onComplete, so a shake retriggered mid-flight must resettle to the
    // ORIGINAL resting position, not wherever it happened to be jittered to when killed.
    const origin = this.shakeOrigins.get(boardContainer) ?? {
      x: boardContainer.position.x,
      y: boardContainer.position.y,
    };
    this.clearScreenShake(boardContainer);
    this.shakeOrigins.set(boardContainer, origin);

    const amplitude = overrides?.amplitude ?? this.config.screenShake.amplitude;
    const durationMs = overrides?.durationMs ?? this.config.screenShake.durationMs;
    const cycles = overrides?.cycles ?? this.config.screenShake.cycles;
    const cycleMs = durationMs / cycles / 1000;
    const timeline = gsap.timeline({
      onComplete: () => {
        boardContainer.position.set(origin.x, origin.y);
        this.shakeTweens.delete(boardContainer);
        this.shakeOrigins.delete(boardContainer);
      },
    });
    for (let i = 0; i < cycles; i += 1) {
      const falloff = 1 - i / cycles;
      const dx = (Math.random() * 2 - 1) * amplitude * falloff;
      const dy = (Math.random() * 2 - 1) * amplitude * falloff;
      timeline.to(boardContainer.position, {
        x: origin.x + dx,
        y: origin.y + dy,
        duration: cycleMs,
        ease: 'sine.inOut',
      });
    }
    timeline.to(boardContainer.position, {
      x: origin.x,
      y: origin.y,
      duration: cycleMs,
      ease: 'sine.out',
    });
    this.shakeTweens.set(boardContainer, timeline);
    return timeline;
  }

  /** Cancel a shake immediately and restore the target's original resting position. */
  clearScreenShake(container: Container): void {
    this.shakeTweens.get(container)?.kill();
    this.shakeTweens.delete(container);
    const origin = this.shakeOrigins.get(container);
    if (origin) container.position.set(origin.x, origin.y);
    this.shakeOrigins.delete(container);
  }

  /**
   * Emit repeated outlined pulses that grow outward from the current reel-frame bounds.
   * The supplied layer stays stable; each finite invocation owns and removes its graphics.
   */
  frameHalo(
    layer: Container,
    bounds: { x: number; y: number; width: number; height: number; cornerRadius: number },
  ): gsap.core.Timeline | undefined {
    this.clearFrameHalo(layer);
    if (bounds.width <= 0 || bounds.height <= 0) return undefined;

    const root = new Container();
    root.eventMode = 'none';
    root.interactiveChildren = false;
    layer.addChild(root);

    const config = this.config.frameHalo;
    const durationSeconds = config.durationMs / 1_000;
    const staggerSeconds = config.staggerMs / 1_000;
    const centerX = bounds.x + bounds.width / 2;
    const centerY = bounds.y + bounds.height / 2;
    const shorterAxis = Math.min(bounds.width, bounds.height);
    const padding = shorterAxis * config.paddingRatio;
    const ringWidth = shorterAxis * config.ringWidthRatio;
    const haloWidth = bounds.width + padding * 2;
    const haloHeight = bounds.height + padding * 2;
    const haloCornerRadius = bounds.cornerRadius + padding;
    const timeline = gsap.timeline({
      onComplete: () => {
        const active = this.frameHalos.get(layer);
        if (active?.root !== root) return;
        this.frameHalos.delete(layer);
        destroyDisplayContainer(root);
      },
    });

    for (let pulse = 0; pulse < config.pulseCount; pulse += 1) {
      const pulseGraphic = new Graphics()
        .roundRect(-haloWidth / 2, -haloHeight / 2, haloWidth, haloHeight, haloCornerRadius)
        .stroke({ color: config.color, alpha: 1, width: ringWidth });
      pulseGraphic.eventMode = 'none';
      pulseGraphic.blendMode = 'add';
      pulseGraphic.position.set(centerX, centerY);
      pulseGraphic.alpha = 0;
      pulseGraphic.scale.set(1);
      root.addChild(pulseGraphic);

      const start = pulse * staggerSeconds;
      timeline.set(pulseGraphic, { alpha: config.alpha }, start);
      timeline.to(
        pulseGraphic.scale,
        {
          x: 1 + config.expansionRatio,
          y: 1 + config.expansionRatio,
          duration: durationSeconds,
          ease: config.ease,
        },
        start,
      );
      timeline.to(pulseGraphic, { alpha: 0, duration: durationSeconds, ease: 'power2.in' }, start);
    }

    this.frameHalos.set(layer, { root, timeline });
    return timeline;
  }

  /** Cancel a frame halo and destroy every transient filled pulse immediately. */
  clearFrameHalo(layer: Container): void {
    const active = this.frameHalos.get(layer);
    if (!active) return;
    active.timeline.kill();
    this.frameHalos.delete(layer);
    destroyDisplayContainer(active.root);
  }

  /** Pulse a full-bleed overlay's alpha up then back to 0 (win-flash punctuation). */
  flash(overlayContainer: Container): gsap.core.Timeline {
    this.clearFlash(overlayContainer);
    overlayContainer.visible = true;
    const { peakAlpha, inMs, outMs } = this.config.flash;
    const timeline = gsap.timeline({
      onComplete: () => {
        overlayContainer.alpha = 0;
        this.flashTweens.delete(overlayContainer);
      },
    });
    timeline
      .to(overlayContainer, { alpha: peakAlpha, duration: inMs / 1000, ease: 'sine.out' })
      .to(overlayContainer, { alpha: 0, duration: outMs / 1000, ease: 'sine.in' });
    this.flashTweens.set(overlayContainer, timeline);
    return timeline;
  }

  /** Cancel a flash immediately and return the overlay to its transparent resting state. */
  clearFlash(overlayContainer: Container): void {
    this.flashTweens.get(overlayContainer)?.kill();
    this.flashTweens.delete(overlayContainer);
    overlayContainer.alpha = 0;
  }
}

function toFilterArray(filters: Container['filters']): Filter[] {
  return filters ? [...filters] : [];
}

function destroyDisplayContainer(container: Container): void {
  container.removeFromParent();
  container.destroy({ children: true });
}

function removeFilter(container: Container, filter: Filter): void {
  const remaining = toFilterArray(container.filters).filter((f) => f !== filter);
  container.filters = remaining.length > 0 ? remaining : null;
}

function setBoundedFilterArea(
  previousAreas: WeakMap<Container, Rectangle | null>,
  container: Container,
  padding: number,
): void {
  previousAreas.set(container, container.filterArea ?? null);
  // Pixi interprets filterArea in the filtered container's local coordinate space and
  // applies worldTransform itself. Using getBounds() here feeds Pixi world coordinates,
  // so translated containers are offset a second time and the filter clips valid content.
  const bounds = container.getLocalBounds();
  const safePadding = Math.max(0, padding);
  container.filterArea = new Rectangle(
    bounds.x - safePadding,
    bounds.y - safePadding,
    Math.max(1, bounds.width + safePadding * 2),
    Math.max(1, bounds.height + safePadding * 2),
  );
}

function restoreFilterArea(
  previousAreas: WeakMap<Container, Rectangle | null>,
  container: Container,
): void {
  const previous = previousAreas.get(container);
  previousAreas.delete(container);
  // Pixi's runtime setter accepts undefined even though its declaration only exposes Rectangle.
  container.filterArea = (previous ?? undefined) as unknown as Rectangle;
}
